/**
 * Seating chart export — every table, every chair, who is in it.
 *
 * Chair-reading logic is not re-derived here. `seatMapFor` and `occupantName`
 * (lib/seatingCandidates.ts) already own the rules for what a chair's
 * assignment resolves to — a blocked chair reads "Kept empty", a plus-one
 * chair reads the guest label, not the claimer's name — and this module would
 * quietly diverge from the seating editor's own display the moment either
 * rule changed and this file did not.
 */
import { attendanceNote, occupantName, seatMapFor } from '../seatingCandidates';
import type { SeatingChartDetail, SeatingChartSummary, SeatingTable } from '../../types/seating';
import { toCsv } from './csv';

export interface SeatingExportRow {
  table: string;
  /** 1-based — the number a host reads off a place card, not the 0-based ordinal. */
  seatNumber: number;
  occupant: string;
  note: string;
}

export interface SeatingTableExport {
  table: string;
  capacity: number;
  filled: number;
  rows: SeatingExportRow[];
}

export const SEATING_EXPORT_HEADERS = ['Table', 'Seat', 'Occupant', 'Note'] as const;

export const OVER_CAPACITY_NOTE = 'Over capacity';

function sortedTables(tables: readonly SeatingTable[]): SeatingTable[] {
  return [...tables].sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Every table in room order, each with every chair — filled or empty — in
 * seat order.
 *
 * The chair count is the LARGER of the table's capacity and its highest
 * assigned seat. A table edited down from 6 to 4 after someone was seated at
 * chair 5 still has that person in chair 5 (`seatMapFor` carries every
 * assignment, capacity-blind), and an export that stopped at 4 would count
 * them in `filled` and then leave them off the printed page. They print, with
 * a note saying why the chair is odd.
 */
export function seatingExportByTable(chart: SeatingChartDetail): SeatingTableExport[] {
  return sortedTables(chart.tables).map((table) => {
    const label = table.label || 'Untitled table';
    const assignments = seatMapFor(table);
    const highestAssigned = Math.max(-1, ...assignments.keys());
    const chairs = Math.max(table.capacity, highestAssigned + 1);
    const rows: SeatingExportRow[] = [];
    for (let seatIndex = 0; seatIndex < chairs; seatIndex += 1) {
      const assignment = assignments.get(seatIndex);
      const notes = [
        assignment ? attendanceNote(assignment.attendanceStatus) : '',
        seatIndex >= table.capacity ? OVER_CAPACITY_NOTE : '',
      ].filter(Boolean);
      rows.push({
        table: label,
        seatNumber: seatIndex + 1,
        occupant: assignment ? occupantName(assignment) : '',
        note: notes.join(' · '),
      });
    }
    return { table: label, capacity: table.capacity, filled: assignments.size, rows };
  });
}

export function seatingChartCsv(chart: SeatingChartDetail): string {
  const rows = seatingExportByTable(chart).flatMap((table) => table.rows);
  return toCsv(
    SEATING_EXPORT_HEADERS,
    rows.map((row) => [row.table, row.seatNumber, row.occupant, row.note]),
  );
}

/** Draft or published, and how many seated guests have since dropped out. */
export function chartStatusLabel(chart: Pick<SeatingChartSummary, 'status' | 'staleAssignmentCount'>): string {
  const status = String(chart.status ?? '').trim().toLowerCase() === 'published' ? 'Published' : 'Draft';
  const stale = chart.staleAssignmentCount ?? 0;
  return stale > 0 ? `${status} · ${stale} no longer coming` : status;
}

/**
 * The picker line. Two charts called "Friday dinner" are told apart by the
 * status the Seating tab's own list shows beside each one — printing a draft
 * as the final plan is the mistake this label exists to catch.
 */
export function chartPickerLabel(chart: Pick<SeatingChartSummary, 'name' | 'status' | 'staleAssignmentCount'>): string {
  return `${chart.name} — ${chartStatusLabel(chart)}`;
}
